import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { Box, BoxProps } from '@chakra-ui/react';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Placeholder } from '@tiptap/extensions/placeholder';
import { Tiptap, useEditor } from '@tiptap/react';

import styles from './editor.module.scss';
import {
  insertSlashCommandChip,
  SlashExtension,
  slashExtensionNodes,
  SuggestionItemGroup,
  useSuggestions,
} from './extensions/SlashExtension';

export type EditorHandle = {
  insertSlashCommand: (item: string, icon?: string) => boolean;
  focus: () => void;
};

export type EditorProps = {
  onChange?: (content: string) => void;
  disabled?: boolean;
  value?: string;
  placeholder?: string;
  children?: React.ReactNode;
  suggestions: SuggestionItemGroup[];
  onSubmit?: () => void;
  /** Extra room for the command picker on the left (default 12px). */
  contentPaddingLeft?: number | string;
  /** Extra room for action buttons on the right (default 48px). */
  contentPaddingRight?: number | string;
} & Omit<BoxProps, 'onChange'>;

const toPadding = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor(
  {
    onChange,
    disabled,
    value,
    placeholder,
    children,
    suggestions: baseSuggestions,
    onSubmit,
    contentPaddingLeft = 12,
    contentPaddingRight = 48,
    ...props
  },
  ref
) {
  const suggestion = useSuggestions(baseSuggestions);
  // Callers often pass a fresh `[]` while a chat turn re-renders. Compare the
  // contents so ProseMirror is not destroyed on the first keystroke.
  const suggestionsKey = JSON.stringify(baseSuggestions);

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const editor = useEditor(
    {
      extensions: [
        Document,
        Paragraph,
        Text,
        HardBreak.extend({
          addKeyboardShortcuts() {
            return {
              'Shift-Enter': () => this.editor.commands.setHardBreak(),
            };
          },
        }),

        ...slashExtensionNodes,

        Placeholder.configure({
          placeholder: placeholder ?? '',
        }),
        SlashExtension.configure({ suggestion }),
      ],

      content: value || '',

      editorProps: {
        handleKeyDown(view, event) {
          const isEnter = event.key === 'Enter';
          const isPlainEnter = isEnter && !event.shiftKey;
          const isSuggestionActive = (view.state as any).suggestion$?.active;

          if (isPlainEnter && !isSuggestionActive) {
            onSubmitRef.current?.();
            return true;
          }
        },
      },
    },
    [suggestionsKey]
  );

  // Texts sent through `onChange` that `value` has not caught up with yet, oldest
  // first. The parent's render for an older text can land after the next
  // keystroke; resetting the document to it would drop that key.
  const pendingEchoesRef = useRef<string[]>([]);

  useEffect(() => {
    if (!editor) return;

    const handler = () => {
      const text = editor.getText();
      pendingEchoesRef.current.push(text);
      onChange?.(text);
    };

    editor.on('update', handler);

    return () => {
      editor.off('update', handler);
    };
  }, [editor, onChange]);

  useEffect(() => {
    if (!editor) return;
    const pending = pendingEchoesRef.current;

    if (editor.getText() === value) {
      pending.length = 0;
      return;
    }

    // An older echo of our own text: skip it and anything sent before it.
    const echoIndex = pending.indexOf(value ?? '');
    if (echoIndex !== -1) {
      pending.splice(0, echoIndex + 1);
      return;
    }

    // A change from outside, such as clearing after send.
    editor.commands.setContent(value || '', { emitUpdate: false });
    pending.length = 0;
  }, [editor, value]);

  useImperativeHandle(
    ref,
    () => ({
      insertSlashCommand: (item, icon) => {
        if (!editor) return false;
        insertSlashCommandChip({ editor, item, icon });
        return true;
      },
      focus: () => {
        editor?.commands.focus();
      },
    }),
    [editor]
  );

  return (
    <Box
      minH='48px'
      maxH='160px'
      borderRadius={12}
      fontSize={16}
      lineHeight={6}
      bg='search-input.bg'
      color='white'
      border='1px solid'
      borderColor='search-input.border!'
      _hover={{
        bg: 'search-input.bg-hover',
        borderColor: 'search-input.border-hover!',
      }}
      _focusWithin={{
        bg: 'search-input.bg-active!',
        borderColor: 'search-input.border-hover!',
      }}
      _disabled={{
        bg: 'search-input.bg-disabled!',
        color: 'search-input.text-disabled!',
        borderColor: 'search-input.border-disabled!',
      }}
      boxShadow='none!'
      position='relative'
      className={styles.editor}
      {...props}
    >
      <Tiptap instance={editor}>
        <Tiptap.Content
          style={{
            maxHeight: '100%',
            overflowY: 'auto',
            padding: `12px ${toPadding(contentPaddingRight)} 12px ${toPadding(
              contentPaddingLeft
            )}`,
          }}
          disabled={disabled || !editor.isInitialized}
        />
      </Tiptap>
      {children}
    </Box>
  );
});
